import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { PRODUCT_FREDHOPPER_SEARCH_PARAM_PLATFORM_OPTIONS, SearchResultsExecutionStatusEnums, } from '@as-react/common-types';
import { capitalizeOnly } from '@as-react/common-utils';
import { Box, ButtonLink, Stack, TextAlpha } from '@yonderland/andes-react';
import { Fragment, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import ProductsAPI from '../api/ProductsAPI';
import { PartialSearchResultsTitles } from '../components/common/partial-search-results-titles/PartialSearchResultsTitles';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
import { partialSearchActions } from '../redux/partialsSearch/actions/PartialsSearchActions.creators';
import { ComparePopupContainer } from './ComparePopupContainer';
import ProductGroupContainer from './product/ProductGroupContainer';
const PartialSearchResultsContainer = () => {
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const aemConfiguration = useAemConfig();
    const { root, feature_toggles } = aemConfiguration;
    const dispatch = useDispatch();
    const [itemCount, setItemCount] = useState({});
    const [shouldRender, setShouldRender] = useState(false);
    const executionStatus = useSelector((state) => state.lister.executionStatus);
    const alternativeQueryResults = useSelector((state) => state.lister.alternativeQueryResults);
    const productGroupUniqueIds = useSelector((state) => state.lister.productGroupUniqueIds);
    const partialSearchResults = useSelector((state) => state.lister.partialSearchResults);
    const queryInformation = useSelector((state) => state.url.q);
    const urlState = useSelector((state) => state.url);
    const productGroup = useSelector((state) => state.productGroup);
    const { alternativeBrand, maybeMeant, maybeLike } = partialSearchResults;
    useEffect(() => {
        dispatch(partialSearchActions.createInitializeAction());
    }, []);
    const hasHeaderValidTitleAndDescription = () => {
        if (executionStatus === SearchResultsExecutionStatusEnums.ALT_BRAND &&
            (alternativeBrand === null || alternativeBrand === void 0 ? void 0 : alternativeBrand.title) &&
            (alternativeBrand === null || alternativeBrand === void 0 ? void 0 : alternativeBrand.description)) {
            return true;
        }
        else if (executionStatus === SearchResultsExecutionStatusEnums.ALT_QUERIES &&
            (maybeMeant === null || maybeMeant === void 0 ? void 0 : maybeMeant.title) &&
            (maybeMeant === null || maybeMeant === void 0 ? void 0 : maybeMeant.description)) {
            return true;
        }
        else if (executionStatus === SearchResultsExecutionStatusEnums.ALT_SPELL_UNCHANGED &&
            (maybeLike === null || maybeLike === void 0 ? void 0 : maybeLike.title) &&
            (maybeLike === null || maybeLike === void 0 ? void 0 : maybeLike.description)) {
            return true;
        }
        return false;
    };
    const isAlternativeBrandSuggestion = () => executionStatus === SearchResultsExecutionStatusEnums.ALT_BRAND;
    const getAlternativeBrandSuggestionTitle = () => alternativeQueryResults.map(item => item.alternativeQuery)[0];
    const getFormattedTitles = productGroupIdKey => {
        const availableTerms = productGroupUniqueIds[productGroupIdKey];
        if (productGroup[productGroupIdKey].length < 1) {
            return null;
        }
        const initialSearchTerms = queryInformation.split(' ');
        const formattedTitles = initialSearchTerms.map(item => {
            return { [item]: availableTerms.includes(item.toLowerCase()) };
        });
        return initialSearchTerms.map((item, index) => !formattedTitles[index][item] ? (_jsxs(Fragment, { children: [_jsx("del", { children: item.trim() }), ' '] }, index)) : (_jsxs("span", { children: [item.trim(), " "] }, index)));
    };
    const getSearchUrl = productGroupKey => `${aemConfiguration.services.fredHopper.search}?mainWebShop=${aemConfiguration.mainWebShop}&fictiveWebShop=${aemConfiguration.fictiveWebShop}&anaLang=${aemConfiguration.lang}&locale=${aemConfiguration.locale}&platform=${aemConfiguration.isShopLanding
        ? PRODUCT_FREDHOPPER_SEARCH_PARAM_PLATFORM_OPTIONS.shoplanding
        : PRODUCT_FREDHOPPER_SEARCH_PARAM_PLATFORM_OPTIONS.public_site}&size=${urlState.size}&page=${urlState.page}&query=${encodeURIComponent(productGroupUniqueIds[productGroupKey])}`;
    useEffect(() => {
        const fetchData = () => {
            let items = {};
            Object.keys(productGroupUniqueIds).map((key) => __awaiter(void 0, void 0, void 0, function* () {
                const searchUrl = getSearchUrl(key);
                const nbrOfItems = yield ProductsAPI.getItemCount(searchUrl);
                items = Object.assign(Object.assign({}, items), { [`${key}`]: nbrOfItems });
                setItemCount(items);
            }));
        };
        fetchData();
    }, []);
    const getLabelText = (productGroupKey) => {
        const searchTerm = capitalizeOnly(productGroupUniqueIds[productGroupKey]);
        return `${t('product.lister.partial.search.button')} ${searchTerm} (${itemCount[productGroupKey]})`;
    };
    useEffect(() => {
        const hasUndefinedItems = Object.keys(productGroupUniqueIds).some(productGroupKey => itemCount[productGroupKey] === undefined);
        if (!hasUndefinedItems) {
            setShouldRender(true);
        }
    }, [itemCount]);
    if (!shouldRender)
        return null;
    return (_jsxs(Box, { children: [hasHeaderValidTitleAndDescription() && (_jsx(PartialSearchResultsTitles, { alternativeBrand: alternativeBrand, maybeMeant: maybeMeant, maybeLike: maybeLike, executionStatus: executionStatus, initialSearchTerm: queryInformation, alternativeQueryResults: alternativeQueryResults })), _jsx(Box, { paddingBlockStart: "10", children: Object.keys(productGroupUniqueIds).map(productGroupKey => (_jsxs("div", { children: [_jsx(TextAlpha, { as: "h1", variant: "headingS", children: isAlternativeBrandSuggestion()
                                ? getAlternativeBrandSuggestionTitle()
                                : getFormattedTitles(`${productGroupKey}`) }), _jsxs(Box, { paddingBlockStart: { base: '6', lg: '10' }, children: [_jsx(ProductGroupContainer, { tileFormat: "normal", collapsible: false, productGroupId: productGroupKey }), _jsx(Box, { paddingBlockEnd: { base: '10', md: '12', lg: '14' }, children: _jsx(Stack, { justify: "center", children: _jsx(ButtonLink, { href: `${root}lister.html?q=${encodeURIComponent(productGroupUniqueIds[productGroupKey])}`, children: getLabelText(productGroupKey) }) }) })] })] }, productGroupKey))) }), feature_toggles.product_compare && _jsx(ComparePopupContainer, {})] }));
};
export default PartialSearchResultsContainer;
